import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Phone, Users, Clock, ChevronDown, ChevronUp, Camera, ArrowRight, ImageIcon, Check, X as XIcon, Star, Crown, Car, Truck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('http') ? url : `${API}${url}`;

const CATEGORY_ORDER = ['concierge', 'group', 'exotic'];
const CATEGORY_LABELS = { concierge: 'Concierge Driven', group: 'Group Driven', exotic: 'Exotic Driven' };
const CATEGORY_ICONS = { concierge: Car, group: Truck, exotic: Crown };

/* ─────────── DATA: TRANSPARENT RATES BAND (top-of-page summary) ─────────── */
const TRANSPARENT_RATES = [
  { type: 'Sedan Service', name: 'Executive Point-to-Point', from: 'Starting From', price: '$85', unit: '/trip', note: 'Per one-way transfer · local Atlanta metro', features: ['Airport pickups & drop-offs', 'Executive daily runs', 'Date nights & dinners', 'Meet-and-greet available', '1–3 passengers'], cta: 'Get a Quote' },
  { type: 'Luxury SUV', name: 'Premium Group Ride', from: 'Starting From', price: '$135', unit: '/trip', note: 'Per one-way transfer · up to 6 passengers', features: ['Airport groups & family runs', 'Client & VIP hosting', 'Weddings & celebrations', 'Golf, galas, shopping', '3–6 passengers'], cta: 'Get a Quote', featured: true },
  { type: 'Sprinter Van', name: 'Group & Event Transport', from: 'Starting From', price: '$225', unit: '/trip', note: 'Per run · up to 14 passengers', features: ['Bachelorette & birthday parties', 'Wine & distillery tours', 'Corporate shuttles', 'Concerts & game day', '8–14 passengers'], cta: 'Get a Quote' },
  { type: 'Hourly Charter', name: 'By-the-Hour Booking', from: 'Starting From', price: '$95', unit: '/hr', note: '2-hour minimum · vehicle class applies', features: ['Multi-stop evenings out', 'Shopping & spa days', 'City tours & experiences', 'Flexible scheduling', 'All vehicle classes available'], cta: 'Get a Quote' },
  { type: 'Corporate Retainer', name: 'Monthly Retainer', from: 'Starting From', price: '$397', unit: '/mo', note: 'Priority booking · dedicated account manager', features: ['Priority scheduling & dispatch', 'Dedicated account manager', 'Monthly billing & reporting', 'Team & executive coverage', 'Discounted rates on all runs'], cta: 'Inquire Now' },
];

const Rates = () => {
  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState(null);
  const [allMedia, setAllMedia] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/pricing/vehicles`).then(r => r.json()),
      fetch(`${API}/api/fleet-media/all`).then(r => r.ok ? r.json() : { vehicles: [] }),
    ]).then(([vehicleData, mediaData]) => {
      const vList = Array.isArray(vehicleData) ? vehicleData : (vehicleData.vehicles || []);
      setVehicles(vList);
      const map = {};
      (mediaData.vehicles || []).forEach(v => { map[v.vehicle_slug || v.vehicle_id] = v; });
      setAllMedia(map);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Group vehicles by category, exclude rentals
  const grouped = {};
  vehicles.forEach(v => {
    const cat = v.category || 'concierge';
    if (cat === 'rental' || cat === 'rental_ke') return;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(v);
  });

  // Sub-group concierge and exotic: sedans first, then SUVs
  const getSubGroups = (items, cat) => {
    if (cat === 'concierge' || cat === 'exotic') {
      const sedans = items.filter(v => (v.tag || v.vehicle_name || '').toLowerCase().includes('sedan'));
      const suvs = items.filter(v => (v.tag || v.vehicle_name || '').toLowerCase().includes('suv'));
      const other = items.filter(v => !sedans.includes(v) && !suvs.includes(v));
      const groups = [];
      if (sedans.length) groups.push({ label: 'Sedans', items: sedans });
      if (suvs.length) groups.push({ label: 'SUVs', items: suvs });
      if (other.length) groups.push({ label: null, items: other });
      return groups;
    }
    return [{ label: null, items }];
  };

  const findMedia = (vehicle) => {
    const slug = vehicle.slug || vehicle.vehicle_id;
    const vName = (vehicle.vehicle_name || '').toLowerCase();
    return Object.values(allMedia).find(m => {
      if (m.vehicle_slug === slug) return true;
      const mName = (m.vehicle_name || '').toLowerCase();
      const mWords = mName.replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(w => w.length > 3);
      const vWords = vName.replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(w => w.length > 3);
      return mWords.some(w => vName.includes(w)) || vWords.some(w => mName.includes(w));
    });
  };

  const VehicleCard = ({ vehicle, idx }) => {
    const isExpanded = expandedCard === idx;
    const mediaEntry = findMedia(vehicle);
    const images = mediaEntry?.images || [];
    const featuredImage = mediaEntry?.featured_image;
    const displayImage = featuredImage ? resolveUrl(featuredImage) : images.length > 0 ? resolveUrl(images[0].url) : null;
    const detailSlug = mediaEntry?.vehicle_slug || vehicle.slug || vehicle.vehicle_id;

    const rate = vehicle.retail_hourly || 0;
    const memberRate = vehicle.member_hourly || rate;
    const discount = rate - memberRate;
    const tag = vehicle.tag || vehicle.vehicle_name;
    const name = vehicle.vehicle_name;
    const pax = vehicle.pax || '4';
    const minHrs = vehicle.min_hours || 0;

    return (
      <Card className="overflow-hidden bg-gray-950 border-gray-800 hover:border-gray-600 transition-all group" data-testid={`rate-card-${idx}`}>
        <div className="h-44 overflow-hidden bg-gray-900 relative cursor-pointer" onClick={() => navigate(`/fleet/${detailSlug}`)}>
          {displayImage ? (
            <img src={displayImage} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
              <Camera className="h-8 w-8 text-gray-600" />
            </div>
          )}
          <div className="absolute top-3 left-3">
            <span className="text-[9px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-2.5 py-1 rounded-full uppercase tracking-wider">{tag}</span>
          </div>
          {images.length > 1 && (
            <span className="absolute bottom-3 right-3 text-[9px] font-bold bg-black/70 backdrop-blur-sm text-gray-300 px-2 py-1 rounded-full flex items-center gap-1">
              <ImageIcon className="h-2.5 w-2.5" /> {images.length} photos
            </span>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="text-sm font-bold text-white group-hover:text-red-500 transition-colors cursor-pointer truncate" onClick={() => navigate(`/fleet/${detailSlug}`)}>{name}</h3>
            {minHrs > 0 && <span className="text-[10px] text-gray-500 flex items-center gap-1 shrink-0"><Clock className="h-3 w-3" /> {minHrs}hr min</span>}
          </div>

          <div className="flex items-end justify-between mb-4 pb-4 border-b border-gray-800">
            <div>
              <div className="flex items-baseline gap-1">
                <p className="text-xl font-black text-white">${rate}</p>
                <p className="text-[10px] text-gray-500">/hr chauffeured</p>
              </div>
              {discount > 0 && <p className="text-[10px] text-red-400 font-semibold">from ${memberRate}/hr member · ${discount} off/hr</p>}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Users className="h-3.5 w-3.5" /> {pax}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <button onClick={() => setExpandedCard(isExpanded ? null : idx)} className="flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-white transition-colors" data-testid={`expand-${idx}`}>
              {isExpanded ? 'Less Details' : 'More Details'}
              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            <button
              onClick={() => navigate(`/booking?vehicle=${detailSlug}`)}
              className="inline-flex items-center gap-1 text-xs font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-full transition-colors"
              data-testid={`book-${idx}`}
            >
              Book <ArrowRight className="h-3 w-3" />
            </button>
          </div>

          {isExpanded && (
            <div className="mt-4 space-y-3 animate-in fade-in">
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-3 space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-gray-500">Passengers</span><span className="font-semibold text-white">{pax}</span></div>
                {minHrs > 0 && <div className="flex justify-between"><span className="text-gray-500">Minimum Booking</span><span className="font-semibold text-white">{minHrs} hours</span></div>}
                <div className="flex justify-between"><span className="text-gray-500">4-Hour Estimate</span><span className="font-semibold text-white">${rate * 4}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">8-Hour (Full Day)</span><span className="font-semibold text-white">${rate * 8}</span></div>
                {discount > 0 && <div className="flex justify-between"><span className="text-gray-500">Member Savings</span><span className="font-semibold text-red-400">${discount} off per hour</span></div>}
              </div>
              <p className="text-[11px] text-gray-500">Rates vary by date, time, and demand. 8% tax + 12% gratuity apply. Member pricing requires active ACE membership.</p>
              <div className="flex gap-2">
                <Button onClick={() => navigate(`/fleet/${detailSlug}`)} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs h-9" data-testid={`view-vehicle-${idx}`}>
                  View Vehicle <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
                <Button onClick={() => navigate('/memberships')} variant="outline" className="flex-1 text-xs h-9 border-gray-700 text-gray-300 hover:bg-gray-900">Membership</Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  let globalIdx = 0;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="rates-page">
      {/* Hero */}
      <div className="bg-gray-950 text-white py-20 sm:py-28 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(220,38,38,0.08),transparent_70%)]" />
        <div className="relative z-10">
          <p className="text-red-500 text-[10px] font-bold tracking-[0.4em] uppercase mb-4">Auto Concierge Enterprises</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4" data-testid="rates-hero-title">Fleet & Pricing</h1>
          <p className="text-gray-400 max-w-lg mx-auto text-sm sm:text-base">Luxury ground transportation built around you. Lobby, Condo & Penthouse members save 8–18%.</p>
          <div className="flex justify-center gap-3 mt-8">
            <a href="tel:6788463009" className="inline-flex items-center h-11 px-6 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
            <Button onClick={() => navigate('/booking')} variant="outline" className="h-11 px-6 border-white/20 text-white hover:bg-white/10 text-sm font-bold">
              Get a Quote <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* TRANSPARENT RATES BAND — top-of-page pricing summary */}
      <section className="bg-black text-white py-16 sm:py-24" data-testid="transparent-rates-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[11px] text-red-500 font-bold tracking-[0.25em] mb-3">PRICING · WHAT TO EXPECT</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] max-w-3xl">
            Transparent Rates.<br /><em className="not-italic text-red-500">No Surprises.</em>
          </h2>
          <p className="text-sm sm:text-base text-gray-400 mt-4 max-w-2xl">
            All quotes are customized to your route, vehicle, and event type. These starting rates give you a clear picture of what ACE-level service looks like.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {TRANSPARENT_RATES.map((p, i) => (
              <Card key={i}
                className={`p-6 ${p.featured ? 'bg-red-950/40 border-red-600/40' : 'bg-white/5 border-white/10'} text-white relative overflow-hidden`}
                data-testid={`transparent-rate-card-${i}`}
              >
                {p.featured && <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-widest bg-red-600 px-2 py-1 rounded-full">Most Popular</span>}
                <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{p.type}</p>
                <p className="text-xl font-bold mt-1.5 mb-3">{p.name}</p>
                <p className="text-[10px] uppercase tracking-widest text-gray-500">{p.from}</p>
                <p className="text-4xl sm:text-5xl font-black mt-1"><span>{p.price}</span><span className="text-sm font-normal text-gray-400">{p.unit}</span></p>
                <p className="text-xs text-gray-400 mt-2 leading-relaxed">{p.note}</p>
                <ul className="space-y-1.5 mt-5">
                  {p.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm text-gray-300"><Check className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/booking')} className="w-full mt-5 bg-white text-black hover:bg-red-500 hover:text-white" data-testid={`transparent-rate-cta-${i}`}>
                  {p.cta}
                </Button>
              </Card>
            ))}

            {/* Multi-Vehicle / Fleet Packages — no POQ price, just CTA */}
            <Card className="p-6 bg-gradient-to-br from-red-700/30 to-red-950/30 border-red-700/40 text-white relative overflow-hidden" data-testid="transparent-rate-card-fleet">
              <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-widest bg-red-600 px-2 py-1 rounded-full">Custom Quote</span>
              <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">Fleet Events</p>
              <p className="text-xl font-bold mt-1.5 mb-3">Multi-Vehicle Packages</p>
              <p className="text-[10px] uppercase tracking-widest text-gray-500">15–100+ Guests</p>
              <p className="text-base text-gray-300 mt-3 mb-2 leading-relaxed">Built for your event — pricing scales with vehicle mix, hours, and coverage.</p>
              <ul className="space-y-1.5 mt-5">
                {['Weddings & receptions', 'Conferences & conventions', 'Film & media production', 'Multi-day event coverage', 'One contact · one invoice'].map((f, fi) => (
                  <li key={fi} className="flex items-start gap-2 text-sm text-gray-300"><Check className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" /> {f}</li>
                ))}
              </ul>
              <Button onClick={() => navigate('/intake/event-booking')} className="w-full mt-5 bg-white text-black hover:bg-red-500 hover:text-white" data-testid="transparent-rate-cta-fleet">
                Request Proposal <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </Card>
          </div>
          <p className="text-[11px] text-gray-500 mt-8 text-center max-w-3xl mx-auto leading-relaxed">
            All rates are base starting prices. Final quotes depend on distance, duration, event type, and vehicle availability. Gratuity not included.
            {' '}<a href="mailto:JD@YOURAUTOCONCIERGE.COM" className="text-red-500 underline">Contact ACE</a> for a custom proposal.
          </p>
        </div>
      </section>

      {/* Vehicle Cards by Category */}
      {loading ? (
        <div className="py-20 text-center"><div className="animate-spin h-8 w-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto" /></div>
      ) : (
        CATEGORY_ORDER.filter(cat => grouped[cat]).map(cat => {
          const label = CATEGORY_LABELS[cat];
          const Icon = CATEGORY_ICONS[cat];
          const isExotic = cat === 'exotic';
          const subGroups = getSubGroups(grouped[cat], cat);

          return (
            <section key={cat} className={`py-14 sm:py-16 ${isExotic ? 'bg-gray-950/60 border-y border-gray-900' : ''}`} data-testid={`section-${cat}`}>
              <div className="max-w-7xl mx-auto px-6">
                <div className="flex items-center gap-3 mb-8">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${isExotic ? 'bg-red-600' : 'bg-gray-900 border border-gray-800'}`}>
                    <Icon className={`h-5 w-5 ${isExotic ? 'text-white' : 'text-red-500'}`} />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white">{label}</h2>
                    <p className="text-xs text-gray-500">{grouped[cat].length} vehicle{grouped[cat].length > 1 ? 's' : ''} · professional chauffeur included</p>
                  </div>
                </div>

                {subGroups.map((sg, sgIdx) => (
                  <div key={sgIdx} className={sgIdx > 0 ? 'mt-10' : ''}>
                    {sg.label && (
                      <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <span className="h-px w-6 bg-gray-700" /> {sg.label}
                      </h3>
                    )}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {sg.items.map(v => {
                        const idx = globalIdx++;
                        return <VehicleCard key={v.vehicle_id || idx} vehicle={v} idx={idx} />;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })
      )}

      {/* Membership CTA */}
      <section className="py-16 border-t border-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Star className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">ACE Membership</h2>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">Priority booking, dedicated account management, and tiered savings on every ride.</p>
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-8">
            {[{ tier: 'Lobby', disc: '8% off' }, { tier: 'Condo', disc: '12% off' }, { tier: 'Penthouse', disc: '18% off' }].map((m, i) => (
              <div key={i} className="bg-gray-950 border border-gray-800 rounded-xl p-4">
                <p className="text-sm font-bold text-white">{m.tier}</p>
                <p className="text-lg font-black text-red-500">{m.disc}</p>
              </div>
            ))}
          </div>
          <Button onClick={() => navigate('/memberships')} className="bg-red-600 hover:bg-red-700 text-white h-11 px-8 text-sm font-bold">
            Become a Member <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Tier Comparison */}
      <section className="py-14 sm:py-20 border-t border-gray-900 bg-gray-950/50" data-testid="tier-comparison">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs text-red-500 font-semibold tracking-widest mb-2">COMPARE</p>
            <h2 className="text-2xl sm:text-3xl font-black text-white">Service Tier Comparison</h2>
          </div>
          <div className="bg-gray-950 rounded-xl border border-gray-800 overflow-hidden">
            <div className="grid grid-cols-4 bg-red-600 text-white">
              <div className="p-4 text-sm font-bold">Feature</div>
              <div className="p-4 text-sm font-bold text-center">Concierge</div>
              <div className="p-4 text-sm font-bold text-center">Group</div>
              <div className="p-4 text-sm font-bold text-center">Exotic</div>
            </div>
            {[
              { label: 'Minimum Hours', concierge: 'No min', group: '4 hrs', exotic: '4 hrs' },
              { label: 'Passengers', concierge: 'Up to 6', group: 'Up to 37', exotic: 'Up to 5' },
              { label: 'Starting Rate', concierge: '$65/hr', group: '$125/hr', exotic: '$200/hr' },
              { label: 'Airport Transfers', concierge: true, group: true, exotic: true },
              { label: 'Corporate Accounts', concierge: true, group: true, exotic: true },
              { label: 'WiFi & Amenities', concierge: true, group: true, exotic: true },
              { label: 'Conference Table', concierge: false, group: true, exotic: false },
              { label: 'Exotic Marques', concierge: false, group: false, exotic: true },
              { label: 'Same-Day Booking', concierge: true, group: true, exotic: false },
              { label: 'Member Discounts', concierge: true, group: true, exotic: false },
            ].map((row, i) => (
              <div key={i} className={`grid grid-cols-4 border-t border-gray-900 ${i % 2 === 0 ? 'bg-gray-950' : 'bg-gray-900/40'}`}>
                <div className="p-3 sm:p-4 text-sm font-medium text-gray-300">{row.label}</div>
                {['concierge', 'group', 'exotic'].map(cat => {
                  const val = row[cat];
                  return (
                    <div key={cat} className="p-3 sm:p-4 text-center">
                      {typeof val === 'boolean' ? (val ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <XIcon className="h-4 w-4 text-gray-700 mx-auto" />) : (
                        <span className="text-sm font-semibold text-white">{val}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <div className="border-t border-gray-900 text-white py-16 text-center">
        <p className="text-[10px] text-red-500 font-bold tracking-[0.3em] uppercase mb-3">Serious Inquiries Only</p>
        <h3 className="text-2xl font-bold mb-2">Call for Immediate Assistance</h3>
        <p className="text-gray-500 text-sm mb-8 max-w-md mx-auto">Custom packages for weddings, corporate events, multi-day engagements, and anything not listed above.</p>
        <div className="flex justify-center gap-3 flex-wrap">
          <a href="tel:6788463009" className="inline-flex items-center h-11 px-8 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold" data-testid="bottom-call-cta">
            <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
          </a>
          <Button onClick={() => navigate('/memberships')} variant="outline" className="h-11 px-8 border-white/20 text-white hover:bg-white/10 text-sm font-bold">
            Become a Member
          </Button>
        </div>
        <p className="text-[9px] text-gray-600 mt-8 max-w-xl mx-auto">All rates are estimates. Final pricing depends on date, distance, availability, and membership tier. 8% sales tax and 12% suggested gratuity apply. Rates subject to change without notice.</p>
      </div>
    </div>
  );
};

export default Rates;
